import React, { useState, useMemo } from 'react';
import { FileText, Brain, Award, Check, Loader2, X } from 'lucide-react';
import { patients } from '../services/api';

const STEPS = [
  { key: 'extract', label: 'Extrair Texto', icon: FileText },
  { key: 'analyze', label: 'Analisar', icon: Brain },
  { key: 'score', label: 'Pontuar', icon: Award },
];

function getCompletedStep(patient) {
  if (!patient) return 0;
  if (patient.laudo_score_report) return 3;
  if (patient.laudo_analysis) return 2;
  if (patient.laudo_clean) return 1;
  return 0;
}

export default function LaudoStepper({ patient, patientId, onComplete }) {
  const [runningStep, setRunningStep] = useState(null);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  const completedStep = useMemo(() => getCompletedStep(patient), [patient]);

  const getStepStatus = (index) => {
    if (error && runningStep === STEPS[index].key) return 'error';
    if (runningStep === STEPS[index].key) return 'active';
    if (index < completedStep) return 'completed';
    if (isRunning && index > STEPS.findIndex(s => s.key === runningStep)) return 'pending';
    return 'pending';
  };

  const handleRun = async () => {
    setIsRunning(true);
    setError(null);
    try {
      // Always run extraction first (even if laudo_clean exists)
      // This ensures we use the latest extraction method (pdftotext + Textract)
      setRunningStep('extract');
      await patients.processLaudo(patientId);
      await onComplete();

      setRunningStep('analyze');
      await patients.analyzeLaudo(patientId);
      await onComplete();

      setRunningStep('score');
      await patients.scoreLaudo(patientId);
      await onComplete();

      setRunningStep(null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao processar');
    } finally {
      setIsRunning(false);
    }
  };

  const getButtonProps = () => {
    if (isRunning) {
      const stepIndex = STEPS.findIndex(s => s.key === runningStep) + 1;
      return {
        label: `Processando... (Etapa ${stepIndex} de 3)`,
        disabled: true,
        className: 'bg-gray-400 cursor-not-allowed',
      };
    }
    if (error) {
      return {
        label: 'Continuar Processamento',
        disabled: false,
        className: 'bg-emerald-600 hover:bg-emerald-700',
      };
    }
    if (completedStep === 0) {
      return {
        label: 'Processar Laudo',
        disabled: false,
        className: 'bg-emerald-600 hover:bg-emerald-700',
      };
    }
    if (completedStep < 3) {
      return {
        label: 'Continuar Processamento',
        disabled: false,
        className: 'bg-emerald-600 hover:bg-emerald-700',
      };
    }
    return {
      label: 'Reprocessar',
      disabled: false,
      className: 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
    };
  };

  const btn = getButtonProps();

  return (
    <div className="space-y-4">
      {/* Stepper */}
      <div className="flex items-center justify-center py-2">
        {STEPS.map((step, i) => {
          const status = getStepStatus(i);
          return (
            <React.Fragment key={step.key}>
              {i > 0 && (
                <div
                  className={`flex-1 h-0.5 mx-1 ${
                    status === 'completed' || (i <= completedStep && !isRunning)
                      ? 'bg-emerald-400'
                      : 'bg-gray-200'
                  }`}
                />
              )}
              <div className="flex flex-col items-center min-w-[80px]">
                <StepCircle status={status} index={i} Icon={step.icon} />
                <span className={`text-xs mt-1.5 font-medium ${
                  status === 'completed' ? 'text-emerald-600' :
                  status === 'active' ? 'text-brand-600' :
                  status === 'error' ? 'text-red-600' :
                  'text-gray-400'
                }`}>
                  {step.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Action button */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleRun}
          disabled={btn.disabled}
          className={`px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-60 ${btn.className} ${
            btn.className.includes('bg-emerald') || btn.className.includes('bg-gray-400')
              ? 'text-white'
              : ''
          }`}
        >
          {isRunning && <Loader2 size={16} className="animate-spin" />}
          {btn.label}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <X size={16} className="flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function StepCircle({ status, index, Icon }) {
  const base = 'w-9 h-9 rounded-full flex items-center justify-center transition-all';

  switch (status) {
    case 'completed':
      return (
        <div className={`${base} bg-emerald-500 text-white`}>
          <Check size={18} />
        </div>
      );
    case 'active':
      return (
        <div className={`${base} bg-brand-500 text-white ring-4 ring-brand-100`}>
          <Loader2 size={18} className="animate-spin" />
        </div>
      );
    case 'error':
      return (
        <div className={`${base} bg-red-500 text-white ring-4 ring-red-100`}>
          <X size={18} />
        </div>
      );
    default:
      return (
        <div className={`${base} bg-gray-200 text-gray-500`}>
          <span className="text-sm font-semibold">{index + 1}</span>
        </div>
      );
  }
}
